import React, {useRef} from 'react'
import {Button, Link, Skeleton as ChakraSkeleton} from '@chakra-ui/react'
import {BasketIcon} from '../icons'
import {FormattedMessage} from 'react-intl'
import PropTypes from 'prop-types'
import {useLocation} from 'react-router-dom'
import {useNotifyMeModal, NotifyModal} from '../../hooks/use-notify-me-modal'
import useOcapiSite from '../../hooks/useOcapiSite'
import {resolveLocaleFromUrl} from '../../utils/utils'
import {noop} from '../../utils/utils'
import {useDerivedProduct} from '../../hooks'
import {useAddToCart} from '../../hooks/use-add-to-cart'
import useMultiSite from '../../hooks/use-multi-site'

// Component Skeleton
export const Skeleton = () => (
    <ChakraSkeleton data-testid="sf-add-to-cart-skeleton" width="100%" height="32px" />
)

const AddToCartCTA = ({
    label,
    product,
    quantity,
    promotions: _promotions,
    includeInAddToCartModal = true,
    showAddToCartModal = true,
    disableNotifyMe = false,
    isLoading: isProductLoading,
    scrollToTop = true,
    onClick = noop,
    onAfterAddToCart = noop,
    onRecommendedProductClick,
    variant = 'solid',
    ...styles
}) => {
    const {site} = useMultiSite()
    const location = useLocation()
    const notifyButtonRef = useRef(null)
    const {id: locale} = resolveLocaleFromUrl(location.pathname)
    const {redirectLinkPartnerAllProducts} = useOcapiSite()
    const redirectToPartner =
        redirectLinkPartnerAllProducts &&
        JSON.parse(redirectLinkPartnerAllProducts)?.[locale.replace('-', '_')]
    const {onOpen: onOpenNotifyMeModal, ...notifyMeModalProps} = useNotifyMeModal()
    const {isOrderable, redirectLinkPartner} = useDerivedProduct(product)
    const {handleAddToCart, isLoading} = useAddToCart(product, {
        isLoading: isProductLoading,
        includeInAddToCartModal,
        quantity,
        showAddToCartModal,
        onRecommendedProductClick
    })
    const isOrderableOnOtherSites = redirectLinkPartner || redirectToPartner

    // content only sites do not render add to cart/notify me buttons
    if (site?.isContentOnlySite && !isOrderableOnOtherSites) {
        return null
    }

    return (
        <>
            {isOrderable || isOrderableOnOtherSites ? (
                <>
                    <Button
                        {...(redirectToPartner
                            ? {
                                  as: Link,
                                  isExternal: true,
                                  href: redirectLinkPartner || redirectToPartner,
                                  onClick
                              }
                            : {
                                  onClick: (e) => {
                                      scrollToTop &&
                                          window.scrollTo({
                                              top: 0,
                                              behavior: 'smooth'
                                          })
                                      onClick?.(e)
                                      handleAddToCart({
                                          onAfterAddToCart,
                                          onRecommendedProductClick
                                      })
                                  }
                              })}
                        key="cart-button"
                        width="100%"
                        variant={variant}
                        rightIcon={<BasketIcon boxSize={4} />}
                        isDisabled={isLoading}
                        isLoading={isLoading}
                        className="add-to-cart"
                        {...styles}
                    >
                        {label || (
                            <FormattedMessage
                                id="product_view.button.add_to_cart"
                                defaultMessage="Add to Cart"
                            />
                        )}
                    </Button>
                </>
            ) : (
                <>
                    {!disableNotifyMe && (
                        <NotifyModal finalFocusRef={notifyButtonRef} {...notifyMeModalProps} />
                    )}

                    <Button
                        key="cart-button"
                        ref={notifyButtonRef}
                        onClick={(e) => {
                            onClick()
                            !disableNotifyMe && onOpenNotifyMeModal(product)
                            e?.preventDefault?.()
                            e?.stopPropagation?.()
                        }}
                        width="100%"
                        variant="solid"
                        data-testid="notify"
                        isDisabled={disableNotifyMe}
                        {...styles}
                    >
                        <FormattedMessage id="notify.modal.submit" defaultMessage="Notify me" />
                    </Button>
                </>
            )}
        </>
    )
}

AddToCartCTA.propTypes = {
    product: PropTypes.object,
    promotions: PropTypes.array,
    quantity: PropTypes.number,
    label: PropTypes.string,
    includeInAddToCartModal: PropTypes.bool,
    showAddToCartModal: PropTypes.bool,
    disableNotifyMe: PropTypes.bool,
    scrollToTop: PropTypes.bool,
    isLoading: PropTypes.bool,
    onClick: PropTypes.func,
    onAfterAddToCart: PropTypes.func,
    onRecommendedProductClick: PropTypes.func,
    variant: PropTypes.string
}
export default AddToCartCTA
